import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Select } from 'antd';
import { Printer, Search, Send } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import { tpApi, useTp } from './tpStore';
import { branchById, fmtMoney, normText } from './hrLogic';
import { fmtMonth } from './tpLogic';
import { PERIOD_STATUS } from './payLogic';
import { Avatar, Badge, PageHeader } from './hrUi';
import { PayslipView, done, useTpPerms } from './tpUi';

/** Phiếu lương từng người của kỳ đã duyệt: xem, in, gửi cho nhân viên */
export default function Payslips() {
  const s = useHr();
  const t = useTp();
  const { canPayEdit, me } = useTpPerms();
  const [params, setParams] = useSearchParams();
  const periods = [...t.periods].filter(p => p.status === 'approved' || p.status === 'paid').sort((a, b) => b.month.localeCompare(a.month));
  const p = periods.find(x => x.id === params.get('id')) || periods[0];
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<string | null>(null);
  const rows = useMemo(() => (p?.rows || []).filter(r => !q || normText(`${r.name} ${r.code}`).includes(normText(q))), [p, q]);
  const r = rows.find(x => x.empId === sel) || rows[0];
  const unsent = p ? p.rows.filter(x => !x.sentAt).length : 0;

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Phiếu lương" desc="Phiếu lương của kỳ đã duyệt — gửi cho từng nhân viên, in hoặc lưu PDF"
          extra={p && <>
            <Select className="w-[200px]" value={p.id} onChange={id => { setParams({ id }, { replace: true }); setSel(null); }}
              options={periods.map(x => ({ value: x.id, label: `${fmtMonth(x.month)} · ${PERIOD_STATUS[x.status].label}` }))} />
            {canPayEdit && <Button type="primary" icon={<Send size={14} />} disabled={!unsent} onClick={() => { const res = tpApi.sendPayslips(p.id, null, me); done(res, `Đã gửi ${res.id} phiếu lương`); }}>Gửi tất cả{unsent ? ` (${unsent})` : ''}</Button>}
          </>} />
        {!p ? <Empty className="py-16" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có kỳ lương đã duyệt" /> : (
          <div className="grid lg:grid-cols-[320px_1fr] gap-4 min-h-0">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm flex flex-col min-h-0">
              <div className="p-3 border-b border-gray-100 dark:border-gray-800">
                <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, mã" value={q} onChange={e => setQ(e.target.value)} />
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-800 overflow-auto max-h-[70vh]">
                {rows.map(x => (
                  <button key={x.empId} type="button" onClick={() => setSel(x.empId)}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm ${r?.empId === x.empId ? 'bg-sky-50 dark:bg-sky-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}>
                    <Avatar name={x.name} />
                    <div className="min-w-0 flex-1"><div className="font-medium truncate">{x.name}</div><div className="text-[11px] text-gray-400 truncate">{x.code} · {branchById(s, x.branchId)?.short}</div></div>
                    <div className="text-right"><div className="text-xs font-semibold">{fmtMoney(x.net)}</div>{x.sentAt ? <span className="text-[10px] text-emerald-600">Đã gửi</span> : <span className="text-[10px] text-gray-400">Chưa gửi</span>}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-5 print:shadow-none">
              {r ? (
                <>
                  <div className="flex justify-end gap-2 mb-3 print:hidden">
                    <Badge className={PERIOD_STATUS[p.status].badge}>{PERIOD_STATUS[p.status].label}</Badge>
                    <Button size="small" icon={<Printer size={13} />} onClick={() => window.print()}>In</Button>
                    {canPayEdit && <Button size="small" icon={<Send size={13} />} onClick={() => done(tpApi.sendPayslips(p.id, [r.empId], me), `Đã gửi phiếu lương cho ${r.name}`)}>{r.sentAt ? 'Gửi lại' : 'Gửi'}</Button>}
                  </div>
                  <div className="max-w-[560px] mx-auto"><PayslipView p={p} r={r} /></div>
                </>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có" />}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
